// lib
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useRef, useLayoutEffect, Fragment } from "react";
import { useUserStore } from "~/stores";
import { useNavigate, useLocation } from "react-router";
import type { Location } from "react-router";

// own containers/components
import Sidebar from "~/components/Shared/Sidebar";
import SystemTimer from "~/components/SystemTimer";
import Notifications from "~/containers/Notifications";

//import footer img
/* import footer from "../../images/footer.svg";
import smartContractingLogo from "~/images/smart-contracting.svg"; */

// stores

// style
import style from "./style.module.scss";
import classNames from "classnames";
import { pathname } from "../../utils";

// Own hooks
import { useSystemAvailability } from "~/hooks/useSystemAvailability";
import { useUser } from "~/hooks/useUser";
import { usePermission } from "~/hooks/usePermission";

import type { Permissions } from "~/types";

/**
 * Props type
 */
interface Props {
    children: React.ReactNode;
}

const Layout = ({ children }: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isInactive =
        location.pathname?.includes("logout") ||
        location.pathname?.includes("inactive");

    // Skip API calls on error pages to prevent infinite loops
    const isErrorPage =
        location.pathname?.includes("/403") ||
        location.pathname?.includes("/401") ||
        location.pathname?.includes("/404");

    const { hasPermissionToEditMaintenance }: Permissions = usePermission();

    const { loading: fetchingUserInfo } = useUser(!isInactive && !isErrorPage);

    const {
        loading: fetchingSystemInfo,
        response: { blockSystemUse, message, active },
    } = useSystemAvailability(!isErrorPage);

    const loading = fetchingUserInfo || fetchingSystemInfo;

    const ref = useRef<HTMLDivElement>(null);

    const { user } = useUserStore();

    useLayoutEffect(() => {
        if (!user || isInactive) return;

        if (!blockSystemUse && pathname?.includes("system-availability")) {
            navigate("/");
            return;
        }

        if (hasPermissionToEditMaintenance) return;

        if (blockSystemUse) {
            navigate("/system-availability", {
                state: { message },
            });
        }
    }, [
        user,
        blockSystemUse,
        hasPermissionToEditMaintenance,
        message,
        isInactive,
    ]);

    /**
     * Render
     */
    return (
        <div className={style.layout}>
            <Notifications />

            <Box className={style.body} ref={ref}>
                <SystemTimer itemRef={ref} />

                <div>
                    {!!user &&
                        !loading &&
                        (!blockSystemUse ||
                            (blockSystemUse &&
                                hasPermissionToEditMaintenance)) && (
                            <div className={style.sidebar}>
                                <Sidebar id="layout" user={user} />
                            </div>
                        )}
                </div>

                <Box
                    className={classNames(style.content, {
                        [style.fullWidth]: !user,
                        [style.noScroll]:
                            (blockSystemUse &&
                                !hasPermissionToEditMaintenance) ||
                            loading,
                    })}
                    tabIndex={0}
                >
                    {loading && (
                        <Box
                            height={"90vh"}
                            justifyContent="center"
                            display="flex"
                            alignItems="center"
                            width={"100%"}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && hasPermissionToEditMaintenance && (
                        <Fragment>
                            {active && (
                                <Box textAlign={"center"} my={1}>
                                    <Typography variant="h4" component="h4">
                                        {message}
                                    </Typography>
                                </Box>
                            )}
                            {children}
                        </Fragment>
                    )}

                    {!loading && !hasPermissionToEditMaintenance && (
                        <Fragment>
                            {!blockSystemUse && active && (
                                <Box textAlign={"center"} my={1}>
                                    <Typography variant="h4" component="h4">
                                        {message}
                                    </Typography>
                                </Box>
                            )}

                            {children}
                        </Fragment>
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default React.memo(Layout);
